import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const TaskStatus = {
    OPEN: 'OPEN',
    IN_PROGRESS: 'IN_PROGRESS',
    DONE: 'DONE',
    BLOCKED: 'BLOCKED'
};

const TaskPriority = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CRITICAL: 'CRITICAL'
};

const TaskModal = ({ task, users, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: TaskStatus.OPEN,
        priority: TaskPriority.MEDIUM,
        assignee: '',
        orderIndex: 0
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (task) {
            setFormData({
                title: task.title || '',
                description: task.description || '',
                status: task.status || TaskStatus.OPEN,
                priority: task.priority || TaskPriority.MEDIUM,
                assignee: task.assignee || '',
                orderIndex: task.orderIndex || 0
            });
        }
    }, [task]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = t('task.taskTitle') + ' ' + t('common.required');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            const payload = {
                title: formData.title.trim(),
                description: formData.description.trim() || undefined,
                status: formData.status,
                priority: formData.priority,
                assignee: formData.assignee || undefined,
                orderIndex: formData.orderIndex
            };
            onSubmit(payload);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-semibold text-[#34495e]">
                        {task ? t('task.editTask') : t('task.createTask')}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-all"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('task.taskTitle')} *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e] ${
                                errors.title ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder={t('task.enterTaskTitle')}
                        />
                        {errors.title && (
                            <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('common.description')}
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                            placeholder={t('task.enterTaskDescription')}
                            rows="4"
                        />
                    </div>

                    {/* Status and Priority */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('common.status')}
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => handleChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                            >
                                <option value={TaskStatus.OPEN}>{t('task.status.open')}</option>
                                <option value={TaskStatus.IN_PROGRESS}>{t('task.status.inProgress')}</option>
                                <option value={TaskStatus.BLOCKED}>{t('task.status.blocked')}</option>
                                <option value={TaskStatus.DONE}>{t('task.status.done')}</option>
                            </select>
                        </div>

                        {/* Priority */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('common.priority')}
                            </label>
                            <select
                                value={formData.priority}
                                onChange={(e) => handleChange('priority', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                            >
                                <option value={TaskPriority.LOW}>{t('task.priority.low')}</option>
                                <option value={TaskPriority.MEDIUM}>{t('task.priority.medium')}</option>
                                <option value={TaskPriority.HIGH}>{t('task.priority.high')}</option>
                                <option value={TaskPriority.CRITICAL}>{t('task.priority.critical')}</option>
                            </select>
                        </div>
                    </div>

                    {/* Assignee */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('common.assignee')}
                        </label>
                        <select
                            value={formData.assignee}
                            onChange={(e) => handleChange('assignee', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#34495e] focus:border-[#34495e]"
                        >
                            <option value="">{t('common.unassigned')}</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || user.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="submit"
                            className="bg-[#34495e] hover:bg-[#2c3e50] text-white font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                        >
                            {task ? t('task.editTask') : t('task.createTask')}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 px-6 rounded-md shadow-sm transition-colors"
                        >
                            {t('common.cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TaskModal;
